/**
 * `usePinnedItems` — the pinned-items data layer for the "For You" tab and the Search page.
 *
 * Built on the shared `useQuery` cache (the same primitive backing `useListSpaces`,
 * `useGetSpace`, etc. — see `@atlassian/conversation-assistant-service/common/utils/use-query`)
 * rather than a React Context provider. The cache is keyed by `cloudId`/`product`, not by
 * where in the component tree this hook is called from, so `PinnedWorkSection` (on the "Your
 * Work" tab) and `RecentWorkResults` (on the Search page) share the same list without any
 * shared ancestor — whichever surface the user opens first triggers the fetch; the other gets
 * a cache hit. `pin`/`unpin` update that same cache entry directly (via `updateCache`) rather
 * than triggering a refetch, so both surfaces reflect a mutation immediately.
 */

import { useCallback, useEffect, useRef } from 'react';

import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';

import type { PinnedItem, SearchWorkItem, UsePinnedItemsResult } from '../types';

import { createPinFetch } from './createPinFetch';
import { deletePinFetch } from './deletePinFetch';
import { listPinsFetch, type Pin } from './listPinsFetch';

// Mirrors the real API's 100-pin/user limit, enforced client-side too.
const MAX_PINNED_ITEMS = 100;

/** Falls back to the last path segment of a pin's URL when the backend has no name for
 * it (`Pin.name` is nullable), rather than showing the full URL. */
const deriveNameFromUrl = (url: string): string => {
	try {
		const segments = new URL(url).pathname.split('/').filter(Boolean);
		return segments[segments.length - 1] ?? url;
	} catch {
		return url;
	}
};

const toPinnedItem = (pin: Pin): PinnedItem => ({
	id: pin.id,
	ari: pin.ari,
	url: pin.url,
	pinType: pin.pinType,
	name: pin.name ?? deriveNameFromUrl(pin.url),
});

export type UsePinnedItemsParams = {
	/** Tenant cloud ID, required to back the list load with the real API. */
	cloudId: string;
	/** Forwarded as the `x-product` header on every pins request. */
	product?: string;
};

export function usePinnedItems({ cloudId, product }: UsePinnedItemsParams): UsePinnedItemsResult {
	// The backend rejects every pins request with no product ("Unknown product. product=")
	// — skip the call entirely rather than firing a request guaranteed to 400. Hosts that
	// don't set a product (e.g. `ForYouPanelProps.product` is optional) just see an empty,
	// non-loading pinned list instead of a failed fetch.
	const hasProduct = Boolean(product);

	const {
		data: pins,
		isFetching: isLoading,
		updateCache,
	} = useQuery<Pin[]>({
		queryKey: ['rovo-pins:listPins', cloudId, product ?? ''],
		queryFn: () => listPinsFetch({ cloudId, product }),
		enabled: hasProduct,
	});

	const rawPins = pins ?? [];

	// `pin`/`unpin` read from this ref rather than `rawPins` directly. `rawPins` is a
	// render-time snapshot — calling `pin()` twice in quick succession (before React has
	// re-rendered with the first mutation's `updateCache` result) would otherwise compute the
	// second mutation from a stale, pre-first-mutation list and clobber it. The ref is updated
	// synchronously inside `pin`/`unpin` themselves, and mirrors `pins` via effect for changes
	// from outside (the initial fetch, or a mutation from another `usePinnedItems` call sharing
	// this same cache entry). Depends on `pins` (the query's own stable value), not the derived
	// `rawPins` — `rawPins` is a fresh `?? []` fallback on every render while `pins` is still
	// null, which would otherwise re-run this effect every render for no reason.
	const latestPinsRef = useRef(rawPins);
	useEffect(() => {
		latestPinsRef.current = pins ?? [];
	}, [pins]);

	/**
	 * Pins an item via the real create endpoint, using the backend-assigned `id`. Moves it to
	 * the top if its `ari` is already pinned instead of duplicating it, capped at
	 * `MAX_PINNED_ITEMS`. Leaves the cache unchanged on failure, so a failed pin doesn't show
	 * as pinned locally when the backend never persisted it.
	 */
	const pin = useCallback(
		async (item: SearchWorkItem) => {
			if (!hasProduct) {
				return;
			}
			let created: Pin;
			try {
				created = await createPinFetch({
					request: { ari: item.ari, url: item.url, pinType: item.pinType, name: item.name },
					cloudId,
					product,
				});
			} catch {
				return;
			}
			// `created.name` is always null on create (see createPinFetch.ts) — fall back to the
			// name the user already saw when they clicked pin, rather than showing a
			// URL-derived name until the next full list refetch resolves it.
			const cached: Pin = { ...created, name: created.name ?? item.name };
			const withoutExisting = latestPinsRef.current.filter((existing) => existing.ari !== item.ari);
			const next = [cached, ...withoutExisting].slice(0, MAX_PINNED_ITEMS);
			latestPinsRef.current = next;
			updateCache(next);
		},
		[cloudId, product, hasProduct, updateCache],
	);

	/** Unpins the item with the given `ari` via the real delete endpoint. No-op if the ari
	 * isn't currently pinned, or the delete fails. */
	const unpin = useCallback(
		async (ari: string) => {
			if (!hasProduct) {
				return;
			}
			const target = latestPinsRef.current.find((existing) => existing.ari === ari);
			if (!target) {
				return;
			}
			try {
				await deletePinFetch({ pinId: target.id, cloudId, product });
			} catch {
				return;
			}
			const next = latestPinsRef.current.filter((existing) => existing.ari !== ari);
			latestPinsRef.current = next;
			updateCache(next);
		},
		[cloudId, product, hasProduct, updateCache],
	);

	return {
		items: rawPins.map(toPinnedItem),
		isLoading,
		pin,
		unpin,
	};
}
